import React, { createContext, useState, useContext, useEffect, useCallback, useRef, ReactNode } from 'react';
import { getSectors, Sector } from '../services/SectorService';
import SessionService from '../services/SessionService';
import appCache from '../services/AppCache';
import { prefetchSessionBootstrap } from '../services/BootstrapService';

interface SectorContextType {
    selectedSectorId: number | null;
    isOfficial: boolean;
    sectors: Sector[];
    isLoading: boolean;
    selectSector: (id: number | null) => void;
    refreshSectors: () => Promise<void>;
}

const SectorContext = createContext<SectorContextType | undefined>(undefined);

export const SectorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [selectedSectorId, setSelectedSectorId] = useState<number | null>(null);
    const [isOfficial, setIsOfficial] = useState<boolean>(false);
    const [sectors, setSectors] = useState<Sector[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const hasRestoredPersistedSector = useRef(false);

    const applySectorSelection = useCallback((sector: Sector) => {
        setSelectedSectorId(sector.id);
        setIsOfficial(sector.isOfficial);
        SessionService.setSectorId(sector.id, sector.isOfficial);
    }, []);

    const clearSectorSelection = useCallback((sectorId?: number | null) => {
        if (sectorId) {
            SessionService.revokeSectorSelection(sectorId);
        } else {
            SessionService.removeSectorId();
        }
        setSelectedSectorId(null);
        setIsOfficial(false);
    }, []);

    const restorePersistedSector = useCallback((fetchedSectors: Sector[]) => {
        if (hasRestoredPersistedSector.current) return;

        const persisted = SessionService.getPersistedSector();
        if (!persisted?.sectorId) {
            hasRestoredPersistedSector.current = true;
            return;
        }

        const sector = fetchedSectors.find((s) => s.id === persisted.sectorId);
        if (sector) {
            applySectorSelection(sector);
            void prefetchSessionBootstrap(sector.id).catch(() => undefined);
        } else {
            console.warn(
                `[SectorContext] Persisted sector ${persisted.sectorId} is no longer available. Revoking selection.`
            );
            clearSectorSelection(persisted.sectorId);
        }

        hasRestoredPersistedSector.current = true;
    }, [applySectorSelection, clearSectorSelection]);

    const refreshSectors = useCallback(async () => {
        const token = SessionService.getToken();
        if (!token) {
            setSectors([]);
            return;
        }

        setIsLoading(true);
        try {
            const fetchedSectors = await getSectors(token, true);
            setSectors(fetchedSectors);
            SessionService.validateAndCleanSectorSession(fetchedSectors.map((s) => s.id));

            if (selectedSectorId) {
                const currentExists = fetchedSectors.find((s) => s.id === selectedSectorId);
                if (!currentExists) {
                    console.warn(`[SectorContext] Sector ${selectedSectorId} no longer exists. Clearing selection.`);
                    clearSectorSelection(selectedSectorId);
                } else if (currentExists.isOfficial !== isOfficial) {
                    setIsOfficial(currentExists.isOfficial);
                    SessionService.setSectorId(selectedSectorId, currentExists.isOfficial);
                }
            } else {
                restorePersistedSector(fetchedSectors);
            }
        } catch (error) {
            console.error('[SectorContext] Error fetching sectors:', error);
            const cached = appCache.get<{ data?: Sector[] }>('sectors');
            if (cached?.data?.length) {
                setSectors(cached.data);
                if (!selectedSectorId) {
                    restorePersistedSector(cached.data);
                }
            }
        } finally {
            setIsLoading(false);
        }
    }, [selectedSectorId, isOfficial, clearSectorSelection, restorePersistedSector]);

    // Initial load — bootstrap with persisted sector when available
    useEffect(() => {
        const token = SessionService.getToken();
        if (!token) return;

        const persistedSectorId = SessionService.getPersistedSector()?.sectorId;

        const bootstrap = async () => {
            if (!appCache.has('bootstrap')) {
                try {
                    await prefetchSessionBootstrap(persistedSectorId ?? undefined);
                } catch {
                    // fallback to sectors-only load
                }
            }
            await refreshSectors();
        };

        void bootstrap();
    }, []);

    // Listen for sector access denied (403) — axios interceptor already revokes persistence/cache
    useEffect(() => {
        const handleSectorAccessDenied = () => {
            setSelectedSectorId(null);
            setIsOfficial(false);
        };
        window.addEventListener('sectorAccessDenied', handleSectorAccessDenied);
        return () => window.removeEventListener('sectorAccessDenied', handleSectorAccessDenied);
    }, []);

    useEffect(() => {
        const handleSectorsUpdated = () => {
            void refreshSectors();
        };
        window.addEventListener('sectorsUpdated', handleSectorsUpdated);
        return () => window.removeEventListener('sectorsUpdated', handleSectorsUpdated);
    }, [refreshSectors]);

    const selectSector = useCallback((id: number | null) => {
        if (id === null) {
            clearSectorSelection();
            return;
        }
        if (!Number.isFinite(id) || id <= 0) {
            return;
        }

        const sector = sectors.find((s) => s.id === id);
        if (sector) {
            applySectorSelection(sector);
            void prefetchSessionBootstrap(sector.id).catch(() => undefined);
        } else {
            console.error(`[SectorContext] Attempted to select invalid sector ID: ${id}`);
        }
    }, [sectors, applySectorSelection, clearSectorSelection]);

    return (
        <SectorContext.Provider value={{
            selectedSectorId,
            isOfficial,
            sectors,
            isLoading,
            selectSector,
            refreshSectors
        }}>
            {children}
        </SectorContext.Provider>
    );
};

export const useSector = () => {
    const context = useContext(SectorContext);
    if (context === undefined) {
        throw new Error('useSector must be used within a SectorProvider');
    }
    return context;
};